import { type ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Semantic status pill with built-in dark-mode-aware classes.
 *
 * Replaces ad-hoc `bg-green-100 text-green-700 border-green-200`-style
 * literals scattered across status maps in Phishing, Gamification,
 * Announcements, etc. Every variant ships with a `dark:` counterpart so
 * the badge survives both themes without further work.
 *
 * Usage:
 *
 *   <StatusBadge variant="success">Active</StatusBadge>
 *   <StatusBadge variant="info">Scheduled</StatusBadge>
 *   <StatusBadge variant="muted">Draft</StatusBadge>
 *   <StatusBadge variant="warning">Archived</StatusBadge>
 *   <StatusBadge variant="destructive">Failed</StatusBadge>
 *
 * If you need a one-off shape (e.g. an LMS tier badge), pass `className`
 * — your override merges over the variant's defaults via cn().
 */
export type StatusVariant =
  | 'success'
  | 'info'
  | 'muted'
  | 'warning'
  | 'destructive'
  | 'highlight' // purple-ish, for "premium / template" categories

const VARIANT_CLASSES: Record<StatusVariant, string> = {
  success:
    'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
  info: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20',
  muted:
    'bg-muted text-muted-foreground border-border',
  warning:
    'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
  destructive:
    'bg-destructive/10 text-destructive border-destructive/20',
  highlight:
    'bg-violet-500/10 text-violet-700 dark:text-violet-400 border-violet-500/20',
}

interface StatusBadgeProps {
  variant: StatusVariant
  children: ReactNode
  className?: string
  /** Optional: render an icon at the leading edge. */
  icon?: ReactNode
}

export function StatusBadge({
  variant,
  children,
  className,
  icon,
}: StatusBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        VARIANT_CLASSES[variant],
        className,
      )}
    >
      {icon}
      {children}
    </span>
  )
}
